import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Wrench } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/lib/seo";
import { api, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import BeforeAfter from "@/components/site/BeforeAfter";

export default function ProjectDetail() {
  const { id } = useParams();
  const { t, lang } = useApp();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/projects/${id}`).then(({ data }) => setP(data)).catch(() => setErr(true)); }, [id]);
  const tx = (obj) => (obj && (obj[lang] || obj.de)) || "";
  useSeo("projects", p ? { title: `${tx(p.title)} | AM-Montage`, desc: tx(p.description).slice(0, 158) } : {});

  if (err) return <div className="pt-40 pb-24 text-center font-display text-2xl">404</div>;
  if (!p) return <div className="pt-40 pb-24 text-center font-display text-xl">…</div>;

  const hasBA = p.before_image && p.after_image;

  return (
    <div className="pt-32 md:pt-40 pb-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/projekte" data-testid="back-to-gallery" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand transition-colors mb-8">
          <ArrowLeft size={16} /> {t.gallery.back}
        </Link>
        <Reveal>
          <span className="text-xs uppercase tracking-widest text-accent2">{t.cats[p.category] || p.category}</span>
          <h1 className="mt-2 font-display font-black text-4xl md:text-6xl tracking-tighter">{tx(p.title)}</h1>
          <div className="mt-6 flex flex-wrap gap-8 text-slate-600">
            {p.location && <div className="flex items-center gap-2"><MapPin size={18} className="text-accent2" /><span><span className="block text-xs uppercase tracking-widest text-slate-400">{t.gallery.location}</span>{p.location}</span></div>}
            {tx(p.scope) && <div className="flex items-center gap-2"><Wrench size={18} className="text-accent2" /><span><span className="block text-xs uppercase tracking-widest text-slate-400">{t.gallery.scope}</span>{tx(p.scope)}</span></div>}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          {hasBA ? (
            <BeforeAfter before={fileUrl(p.before_image)} after={fileUrl(p.after_image)} labelBefore={t.gallery.before} labelAfter={t.gallery.after} />
          ) : (
            <div className="aspect-[16/9] overflow-hidden border border-slate-200">
              <img src={fileUrl(p.cover_image || p.after_image || p.before_image)} alt={tx(p.title)} className="w-full h-full object-cover" />
            </div>
          )}
        </Reveal>

        {tx(p.description) && (
          <Reveal delay={0.15} className="mt-12 max-w-2xl">
            <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-line">{tx(p.description)}</p>
          </Reveal>
        )}

        {p.gallery?.length > 0 && (
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {p.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.05} className="aspect-[4/3] overflow-hidden border border-slate-200">
                <img src={fileUrl(g)} alt="" className="w-full h-full object-cover" />
              </Reveal>
            ))}
          </div>
        )}

        <div className="mt-16 pt-10 border-t border-slate-200 text-center">
          <Link to="/kontakt" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-7 py-4 hover:bg-accent2 transition-colors">
            {t.nav.quote}
          </Link>
        </div>
      </div>
    </div>
  );
}
